import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Input } from "@oc-labs/wt-storybook";
import ModalV2 from "../ModalV2";
import TagInput from "../TagInput";
import Toggle from "../Toggle";
import CodeEditor from "../CodeEditor";
import useModal from "../../hooks/useModal";
import { Upload, Plus, Trash2, Sparkles, Send, Bot, User } from "lucide-react";

const ARG_TYPES = ["string", "number", "address", "bool"];

const coerceArg = ({ type, value }) => {
    if (type === "number") {
        if (value === "" || value == null) return 0;
        const n = Number(value);
        if (Number.isNaN(n)) throw new Error(`"${value}" is not a valid number`);
        return n;
    }
    if (type === "bool") return value === "true" || value === true;
    return String(value ?? "");
};

const newArg = (overrides = {}) => ({
    id: crypto.randomUUID(),
    type: "string",
    value: "",
    name: "",
    solidityType: "",
    ...overrides,
});

const stripSolidityComments = (src) =>
    src.replace(/\/\/.*$/gm, "").replace(/\/\*[\s\S]*?\*\//g, "");

const solidityTypeToArgType = (solType) => {
    if (!solType) return "string";
    const t = solType.replace(/\s+payable$/, "").trim();
    if (t === "address") return "address";
    if (t === "bool") return "bool";
    if (/^(u?int)(\d*)$/.test(t)) return "number";
    return "string";
};

const parseConstructorParams = (paramsStr) => {
    const raw = paramsStr.trim();
    if (!raw) return [];
    const parts = [];
    let depth = 0;
    let buf = "";
    for (const ch of raw) {
        if (ch === "(" || ch === "[") depth++;
        else if (ch === ")" || ch === "]") depth--;
        if (ch === "," && depth === 0) {
            parts.push(buf.trim());
            buf = "";
        } else {
            buf += ch;
        }
    }
    if (buf.trim()) parts.push(buf.trim());

    return parts.map((p) => {
        const cleaned = p.replace(/\b(memory|calldata|storage)\b/g, " ").replace(/\s+/g, " ").trim();
        const tokens = cleaned.split(" ");
        let name = "";
        let solType = cleaned;
        if (tokens.length >= 2) {
            name = tokens[tokens.length - 1];
            solType = tokens.slice(0, -1).join(" ");
        }
        return {
            name,
            solidityType: solType,
            type: solidityTypeToArgType(solType),
        };
    });
};

const extractConstructorArgs = (source, contractName) => {
    if (!source) return null;
    const clean = stripSolidityComments(source);

    const ctorRegex = /constructor\s*\(/g;
    const candidates = [];
    let m;
    while ((m = ctorRegex.exec(clean)) !== null) {
        let i = m.index + m[0].length;
        let depth = 1;
        while (i < clean.length && depth > 0) {
            const ch = clean[i];
            if (ch === "(") depth++;
            else if (ch === ")") {
                depth--;
                if (depth === 0) break;
            }
            i++;
        }
        if (depth !== 0) continue;
        const paramsStr = clean.slice(m.index + m[0].length, i);

        const before = clean.slice(0, m.index);
        const contractMatches = [...before.matchAll(/\bcontract\s+(\w+)/g)];
        const enclosing = contractMatches.length ? contractMatches[contractMatches.length - 1][1] : "";

        candidates.push({ paramsStr, enclosing });
    }

    if (!candidates.length) return [];

    const preferred =
        (contractName && candidates.find((c) => c.enclosing === contractName)) ||
        candidates[candidates.length - 1];

    return parseConstructorParams(preferred.paramsStr);
};

export default function ModalDeployToken({ networks = [], defaultNetworkId = null, onDeploy, onGenerate }) {
    const modal = useModal("deployToken");
    const sourceFileRef = useRef(null);

    const [step, setStep] = useState(1);
    const [chatMessages, setChatMessages] = useState([]);
    const [chatInput, setChatInput] = useState("");
    const [generating, setGenerating] = useState(false);
    const [contractType, setContractType] = useState("ERC20");

    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [symbol, setSymbol] = useState("");
    const [decimals, setDecimals] = useState("18");
    const [networkId, setNetworkId] = useState(defaultNetworkId || "");
    const [logo, setLogo] = useState("");
    const [tags, setTags] = useState([]);
    const [fixedPrice, setFixedPrice] = useState("");
    const [buyable, setBuyable] = useState(false);

    const fixedPriceValid = fixedPrice === "" || /^\d+(\.\d{1,18})?$/.test(fixedPrice);

    const [sourceCode, setSourceCode] = useState("");
    const [sourceFileName, setSourceFileName] = useState("");
    const [sourceError, setSourceError] = useState("");
    const [args, setArgs] = useState([]);
    const [detected, setDetected] = useState(null);

    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        setNetworkId(defaultNetworkId || "");
    }, [defaultNetworkId]);

    const handleClose = () => {
        setStep(1);
        setName("");
        setDescription("");
        setSymbol("");
        setDecimals("18");
        setNetworkId(defaultNetworkId || "");
        setLogo("");
        setTags([]);
        setFixedPrice("");
        setBuyable(false);
        setSourceCode("");
        setSourceFileName("");
        setSourceError("");
        setArgs([]);
        setDetected(null);
        setSubmitting(false);
        setChatMessages([]);
        setChatInput("");
        setGenerating(false);
        setContractType("ERC20");
        modal.close();
    };

    const detectArgs = (source, contractName) => {
        const parsed = extractConstructorArgs(source, contractName);
        if (!parsed) {
            setDetected({ ok: false, reason: "empty" });
            setArgs([]);
            return;
        }
        if (parsed.length === 0) {
            setDetected({ ok: true, count: 0 });
            setArgs([]);
            return;
        }
        setArgs(
            parsed.map((p) =>
                newArg({ type: p.type, name: p.name, solidityType: p.solidityType })
            )
        );
        setDetected({ ok: true, count: parsed.length });
    };

    const handleSendChat = async () => {
        const prompt = chatInput.trim();
        if (!prompt || generating || !onGenerate) return;
        setChatInput("");
        const userMsg = { role: "user", content: prompt };
        setChatMessages((prev) => [...prev, userMsg]);
        setGenerating(true);
        try {
            const isEdit = Boolean(sourceCode.trim());
            const res = await onGenerate({
                prompt,
                name: name.trim() || undefined,
                contractType: contractType || undefined,
                sourceCode: isEdit ? sourceCode : undefined,
            });
            const generated = (res?.sourceCode || res?.source || "").replace(/\s+$/, "");
            if (generated) {
                setSourceCode(generated);
                setSourceError("");
                detectArgs(generated, name.trim());
                setChatMessages((prev) => [
                    ...prev,
                    {
                        role: "assistant",
                        content: `${isEdit ? "Updated" : "Generated"} contract (${generated.length.toLocaleString()} chars). ${isEdit ? "Edits applied to the editor." : "Loaded into the editor."} Refine by chatting again.`,
                    },
                ]);
            } else {
                setChatMessages((prev) => [
                    ...prev,
                    { role: "assistant", content: "No source code returned. Try rephrasing your prompt." },
                ]);
            }
        } catch (err) {
            console.error("Error generating contract:", err);
            setChatMessages((prev) => [
                ...prev,
                {
                    role: "assistant",
                    content: err?.response?.data?.message || err.message || "Generation failed.",
                },
            ]);
        } finally {
            setGenerating(false);
        }
    };

    const handleSourceUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (evt) => {
            const raw = evt.target.result || "";
            if (typeof raw !== "string" || !raw.trim()) {
                setSourceError("File appears to be empty");
                return;
            }
            const text = raw.replace(/\s+$/, "");
            setSourceCode(text);
            setSourceFileName(file.name);
            setSourceError("");
            detectArgs(text, name.trim() || file.name.replace(/\.sol$/i, ""));
        };
        reader.readAsText(file);
    };

    const handleDetectFromSource = () => {
        if (!sourceCode.trim()) return;
        detectArgs(sourceCode, name.trim() || sourceFileName.replace(/\.sol$/i, ""));
    };

    const isInfoValid = Boolean(name.trim() && symbol.trim() && networkId && fixedPriceValid);
    const isCodeValid = Boolean(sourceCode.trim());
    const isValid = isInfoValid && isCodeValid;

    const coercedPreview = useMemo(() => {
        try {
            return { ok: true, value: args.map(coerceArg) };
        } catch (err) {
            return { ok: false, error: err.message };
        }
    }, [args]);

    const handleSubmit = async () => {
        if (!isValid) return;
        if (!coercedPreview.ok) return;
        setSubmitting(true);
        try {
            await onDeploy({
                name: name.trim(),
                description: description.trim(),
                symbol: symbol.trim().toUpperCase(),
                decimals: parseInt(decimals) || 18,
                networkId,
                logo: logo.trim() || undefined,
                tags,
                fixedPrice: fixedPrice === "" ? null : Number(fixedPrice),
                buyable,
                sourceCode,
                constructorArguments: coercedPreview.value,
            });
            handleClose();
        } catch (err) {
            console.error("Error deploying token:", err);
            setSubmitting(false);
        }
    };

    const stepTitle =
        step === 1
            ? "Deploy Token — Details"
            : step === 2
            ? "Deploy Token — Source Code"
            : "Deploy Token — Constructor & Confirm";

    const modalClass =
        step === 2
            ? "w-[min(1100px,95vw)] max-h-[90vh] overflow-y-auto"
            : "w-[min(640px,95vw)] max-h-[90vh] overflow-y-auto";

    const StepBadge = ({ n, label }) => (
        <span className={`flex items-center gap-1.5 ${step === n ? "text-blue-600 font-medium" : ""}`}>
            <span
                className={`w-5 h-5 rounded-full flex items-center justify-center text-[11px] ${
                    step === n ? "bg-blue-600 text-white" : "bg-slate-200 text-slate-600"
                }`}
            >
                {n}
            </span>
            {label}
        </span>
    );

    return (
        <ModalV2 name="deployToken" title={stepTitle} className={modalClass}>
            <div className="mt-2 mb-4 flex items-center gap-2 text-xs text-slate-500">
                <StepBadge n={1} label="Details" />
                <span className="flex-1 h-px bg-slate-200" />
                <StepBadge n={2} label="Source Code" />
                <span className="flex-1 h-px bg-slate-200" />
                <StepBadge n={3} label="Constructor & Confirm" />
            </div>

            {step === 1 && (
                <div className="space-y-4">
                    <Input
                        label="Token Name (must match the Solidity contract name)"
                        placeholder="e.g. MyToken"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                    />
                    <Input
                        label="Description"
                        placeholder="Brief description of the token"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                    />
                    <div className="grid grid-cols-2 gap-3">
                        <Input
                            label="Symbol"
                            placeholder="e.g. MTK"
                            value={symbol}
                            onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                        />
                        <Input
                            label="Decimals"
                            type="number"
                            placeholder="18"
                            value={decimals}
                            onChange={(e) => setDecimals(e.target.value)}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Network</label>
                        <select
                            value={networkId}
                            onChange={(e) => setNetworkId(e.target.value)}
                            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="">Select a network</option>
                            {networks.map((n) => (
                                <option key={n.id} value={n.id}>{n.name}</option>
                            ))}
                        </select>
                    </div>
                    <Input
                        label="Logo URL"
                        placeholder="https://example.com/logo.png"
                        value={logo}
                        onChange={(e) => setLogo(e.target.value)}
                    />
                    <div>
                        <Input
                            label="Fixed price (USD)"
                            type="text"
                            inputMode="decimal"
                            placeholder="e.g. 0.045"
                            value={fixedPrice}
                            onChange={(e) => setFixedPrice(e.target.value)}
                        />
                        <p className="text-xs text-slate-500 mt-1">
                            Set a manual USD price for tokens without a price oracle. Leave empty to use the default price source.
                        </p>
                        {!fixedPriceValid && (
                            <p className="text-xs text-red-500 mt-1">Must be a non-negative number with up to 18 decimal places.</p>
                        )}
                    </div>
                    <TagInput label="Tags" value={tags} onChange={setTags} />
                    <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200">
                        <div className="flex flex-col">
                            <span className="text-slate-900 text-sm font-medium">Buyable</span>
                            <span className="text-slate-500 text-xs">Available for purchase</span>
                        </div>
                        <Toggle checked={buyable} onChange={setBuyable} text="Buyable" />
                    </div>
                </div>
            )}

            {step === 2 && (
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                    <div className="lg:col-span-3 flex flex-col">
                        <div className="flex items-center justify-between mb-1">
                            <label className="block text-sm font-medium text-slate-700">Solidity Source</label>
                            <input
                                ref={sourceFileRef}
                                type="file"
                                accept=".sol,text/plain"
                                onChange={handleSourceUpload}
                                className="hidden"
                            />
                            <button
                                type="button"
                                onClick={() => sourceFileRef.current?.click()}
                                className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-xs text-slate-600 hover:border-blue-400 hover:text-blue-600 transition-colors"
                            >
                                <Upload size={12} />
                                {sourceFileName ? "Replace .sol file" : "Upload .sol file"}
                            </button>
                        </div>
                        <div className="h-[480px]">
                            <CodeEditor
                                value={sourceCode}
                                onChange={(next) => {
                                    setSourceCode(next);
                                    setSourceError("");
                                }}
                                language="solidity"
                                placeholder={"// SPDX-License-Identifier: MIT\npragma solidity ^0.8.20;\n\ncontract MyToken {\n  // ...\n}"}
                                fillHeight
                            />
                        </div>
                        {sourceError && <p className="text-xs text-red-500 mt-1">{sourceError}</p>}
                        {sourceCode && !sourceError && (
                            <p className="text-xs text-slate-500 mt-1">
                                {sourceFileName ? `Loaded ${sourceFileName}. ` : ""}
                                {sourceCode.length.toLocaleString()} chars. Imports allowed: <code>@openzeppelin/...</code>
                            </p>
                        )}
                    </div>

                    <div className="lg:col-span-2 flex flex-col rounded-lg border border-purple-200 bg-purple-50/40 h-[480px]">
                        <div className="flex items-center justify-between px-3 py-2 border-b border-purple-200">
                            <span className="flex items-center gap-2 text-sm font-medium text-purple-800">
                                <Sparkles size={14} />
                                Build with AI
                            </span>
                            <select
                                value={contractType}
                                onChange={(e) => setContractType(e.target.value)}
                                className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs"
                                disabled={!onGenerate}
                            >
                                <option value="ERC20">ERC20</option>
                                <option value="ERC721">ERC721</option>
                                <option value="ERC1155">ERC1155</option>
                                <option value="">Other</option>
                            </select>
                        </div>

                        <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-2">
                            {!onGenerate && (
                                <p className="text-xs text-slate-500 italic">
                                    AI generation is not available in this context.
                                </p>
                            )}
                            {onGenerate && chatMessages.length === 0 && !generating && (
                                <p className="text-xs text-slate-500 italic">
                                    Describe the contract you want — for example: &quot;An ERC20 with a 2% transfer
                                    fee sent to the owner, mintable by the owner only.&quot;
                                </p>
                            )}
                            {chatMessages.map((m, i) => (
                                <div
                                    key={i}
                                    className={`flex gap-2 text-xs ${m.role === "user" ? "justify-end" : "justify-start"}`}
                                >
                                    {m.role === "assistant" && (
                                        <Bot size={14} className="shrink-0 mt-0.5 text-purple-600" />
                                    )}
                                    <div
                                        className={`rounded-lg px-2 py-1.5 max-w-[85%] whitespace-pre-wrap ${
                                            m.role === "user"
                                                ? "bg-blue-600 text-white"
                                                : "bg-slate-100 text-slate-800"
                                        }`}
                                    >
                                        {m.content}
                                    </div>
                                    {m.role === "user" && (
                                        <User size={14} className="shrink-0 mt-0.5 text-blue-600" />
                                    )}
                                </div>
                            ))}
                            {generating && (
                                <div className="flex items-center gap-2 text-xs text-slate-500">
                                    <Bot size={14} className="text-purple-600" />
                                    <span className="italic">Generating contract…</span>
                                </div>
                            )}
                        </div>

                        <div className="border-t border-purple-200 p-3 space-y-2">
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={chatInput}
                                    onChange={(e) => setChatInput(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" && !e.shiftKey) {
                                            e.preventDefault();
                                            handleSendChat();
                                        }
                                    }}
                                    disabled={generating || !onGenerate}
                                    placeholder="Describe the contract…"
                                    className="flex-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-slate-100"
                                />
                                <button
                                    type="button"
                                    onClick={handleSendChat}
                                    disabled={generating || !chatInput.trim() || !onGenerate}
                                    className="inline-flex items-center gap-1 rounded-md bg-purple-600 px-3 py-2 text-sm text-white hover:bg-purple-700 disabled:bg-slate-300 disabled:cursor-not-allowed"
                                >
                                    <Send size={14} />
                                    Send
                                </button>
                            </div>
                            <p className="text-[11px] text-slate-500">
                                {sourceCode
                                    ? "Editing mode — current source is sent so the AI can modify it."
                                    : name.trim()
                                    ? `Name hint: ${name.trim()}`
                                    : "Tip: set a Token Name in step 1 for better results."}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {step === 3 && (
                <div className="space-y-4">
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <label className="block text-sm font-medium text-slate-700">Constructor Arguments</label>
                            <div className="flex items-center gap-3">
                                <button
                                    type="button"
                                    onClick={handleDetectFromSource}
                                    disabled={!sourceCode.trim()}
                                    className="text-xs text-blue-600 hover:text-blue-800 disabled:text-slate-400 disabled:cursor-not-allowed"
                                >
                                    Detect from source
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setArgs([...args, newArg()])}
                                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                                >
                                    <Plus size={14} /> Add argument
                                </button>
                            </div>
                        </div>

                        {detected?.ok && detected.count > 0 && (
                            <p className="text-xs text-blue-700 mb-2">
                                Auto-filled {detected.count} argument{detected.count === 1 ? "" : "s"} from the
                                contract&apos;s constructor. Review values before deploying.
                            </p>
                        )}
                        {detected?.ok && detected.count === 0 && (
                            <p className="text-xs text-slate-500 italic mb-2">
                                Constructor takes no parameters (or none detected). Add any manually if needed.
                            </p>
                        )}
                        {detected && !detected.ok && (
                            <p className="text-xs text-amber-700 mb-2">
                                Couldn&apos;t auto-detect constructor from source. Add arguments manually.
                            </p>
                        )}
                        {!detected && args.length === 0 && (
                            <p className="text-xs text-slate-500 italic mb-2">
                                No arguments. Click &quot;Detect from source&quot; or &quot;Add argument&quot; if your
                                constructor takes parameters.
                            </p>
                        )}

                        <div className="flex flex-col gap-2">
                            {args.map((arg, idx) => (
                                <div key={arg.id} className="flex items-center gap-2">
                                    <span className="text-xs text-slate-500 w-6 text-right">{idx}</span>
                                    <select
                                        className="rounded-lg border border-slate-300 bg-white h-10 px-3 text-sm"
                                        value={arg.type}
                                        onChange={(e) =>
                                            setArgs(args.map((a) => (a.id === arg.id ? { ...a, type: e.target.value, value: "" } : a)))
                                        }
                                    >
                                        {ARG_TYPES.map((t) => (
                                            <option key={t} value={t}>{t}</option>
                                        ))}
                                    </select>
                                    {arg.type === "bool" ? (
                                        <select
                                            className="flex-1 rounded-lg border border-slate-300 bg-white h-10 px-3 text-sm"
                                            value={String(arg.value)}
                                            onChange={(e) =>
                                                setArgs(args.map((a) => (a.id === arg.id ? { ...a, value: e.target.value } : a)))
                                            }
                                        >
                                            <option value="">— select —</option>
                                            <option value="true">true</option>
                                            <option value="false">false</option>
                                        </select>
                                    ) : (
                                        <input
                                            className="flex-1 rounded-lg border border-slate-300 bg-white h-10 px-3 text-sm font-mono"
                                            type={arg.type === "number" ? "number" : "text"}
                                            placeholder={
                                                arg.name
                                                    ? `${arg.solidityType || arg.type} ${arg.name}`
                                                    : arg.type === "address"
                                                    ? "0x..."
                                                    : arg.type === "number"
                                                    ? "18"
                                                    : "value"
                                            }
                                            value={arg.value}
                                            onChange={(e) =>
                                                setArgs(args.map((a) => (a.id === arg.id ? { ...a, value: e.target.value } : a)))
                                            }
                                        />
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => setArgs(args.filter((a) => a.id !== arg.id))}
                                        className="p-2 text-slate-400 hover:text-red-600 transition"
                                        aria-label="Remove argument"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            ))}
                        </div>
                        {!coercedPreview.ok && (
                            <p className="text-xs text-red-500 mt-2">{coercedPreview.error}</p>
                        )}
                    </div>

                    <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-800">
                        We&apos;ll compile and deploy in the background. Your token row will appear immediately as
                        <span className="font-medium"> pending</span> and update once the address is on-chain (typically under a minute on testnets).
                    </div>
                </div>
            )}

            <div className="mt-6 flex gap-2">
                {step === 1 && (
                    <>
                        <Button type="tertiary" onClick={handleClose} className="w-1/2">
                            Cancel
                        </Button>
                        <Button
                            type="primary"
                            onClick={() => setStep(2)}
                            className="w-1/2"
                            disabled={!isInfoValid}
                        >
                            Next
                        </Button>
                    </>
                )}
                {step === 2 && (
                    <>
                        <Button type="tertiary" onClick={() => setStep(1)} className="w-1/2">
                            Back
                        </Button>
                        <Button
                            type="primary"
                            onClick={() => setStep(3)}
                            className="w-1/2"
                            disabled={!isCodeValid}
                        >
                            Next
                        </Button>
                    </>
                )}
                {step === 3 && (
                    <>
                        <Button type="tertiary" onClick={() => setStep(2)} className="w-1/2">
                            Back
                        </Button>
                        <Button
                            type="primary"
                            onClick={handleSubmit}
                            className="w-1/2"
                            disabled={submitting || !isValid || !coercedPreview.ok}
                        >
                            {submitting ? "Submitting..." : "Deploy Token"}
                        </Button>
                    </>
                )}
            </div>
        </ModalV2>
    );
}
